// Shared modal/dialog primitive. Previously each platform workflow page (WorkflowEditorPage,
// WorkflowDefinitionPage) defined its own near-identical inline modal markup independently —
// this consolidates that into one reusable component so new UI (e.g. the Governance &
// Automation Hooks "Add" dialogs) doesn't duplicate it again.
import React from 'react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/UI/Button';

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Omit to render a plain dialog with only the close (×) affordance — e.g. read-only content. */
  onSave?: () => void;
  saving?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  saveDisabled?: boolean;
  maxWidthClassName?: string;
  /** Optional small note shown left-aligned in the footer, alongside Cancel/Save — e.g. a
   * caveat about when the condition is re-evaluated. Omit for the plain right-aligned buttons. */
  footerLeft?: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
  title, onClose, children, onSave, saving, saveLabel = 'Save', cancelLabel = 'Cancel',
  saveDisabled, maxWidthClassName = 'max-w-lg', footerLeft,
}) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div className={cn('w-full mx-4 flex max-h-[85vh] flex-col rounded-2xl border border-ui-border2 bg-ui-card shadow-2xl', maxWidthClassName)}>
      <div className="flex items-center justify-between border-b border-ui-border px-5 py-4">
        <h2 className="text-sm font-semibold text-ui-text">{title}</h2>
        <button onClick={onClose} className="text-lg leading-none text-ui-text3 hover:text-ui-text" aria-label="Close">×</button>
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">{children}</div>
      {onSave && (
        <div className={cn('flex items-center border-t border-ui-border px-5 py-3', footerLeft ? 'justify-between' : 'justify-end')}>
          {footerLeft && <p className="text-xs text-ui-text3">{footerLeft}</p>}
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>{cancelLabel}</Button>
            <Button size="sm" onClick={onSave} disabled={saving || saveDisabled} loading={saving}>{saveLabel}</Button>
          </div>
        </div>
      )}
    </div>
  </div>
);

export default Modal;
